import React, { Ref, useRef, useState } from "react";
import { ErrorMessage, useField, useFormikContext } from "formik";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import { PolyIcon } from "elements";
import { isSameDay as dfIsSameDay, set as dfSet } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

const BASE_RANGE_TIME = new Date();

const MIN_RANGE_TIME: Date = dfSet(BASE_RANGE_TIME, {
  hours: 0,
  minutes: 0,
  seconds: 0,
  milliseconds: 0
});

const MAX_RANGE_TIME: Date = dfSet(BASE_RANGE_TIME, {
  hours: 23,
  minutes: 59,
  seconds: 59,
  milliseconds: 999
});

function isSameDay(date1: Date | null, date2: Date | null): boolean {
  if (date1 && date2) {
    return dfIsSameDay(date1, date2);
  } else {
    return !date1 && !date2;
  }
}

/**
 * When using a custom input, DatePicker needs DOM access to the child component and as a result, a React.Component
 * or use React.forwardRef with a functional component must be used in order to avoid the following error message:
 *
 * "Function components cannot be given refs. Attempts to access this ref will fail. Did you mean to
 * use React.forwardRef()?"
 *
 * Using React.forwardRef with a functional component breaks the input focus feature and thus a React.Component is
 * used instead.
 *
 * See:
 * https://reactjs.org/docs/refs-and-the-dom.html
 * https://reactjs.org/docs/forwarding-refs.html
 *
 */
export enum DatePickerRangeType {
  NONE,
  START,
  END
}

export interface DatePickerProps {
  name: string;
  label?: string;
  otherName?: string;
  rangeType?: DatePickerRangeType;
  tabIndex?: number;
  minDate?: Date;
  maxDate?: Date;
  minTime?: Date;
  maxTime?: Date;
  showMonthDropdown?: boolean;
  showTimeSelect?: boolean;
  showYearDropdown?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  name,
  otherName,
  tabIndex,
  minDate,
  maxDate,
  minTime,
  maxTime,
  disabled,
  readOnly,
  rangeType = DatePickerRangeType.NONE,
  showMonthDropdown = true,
  showTimeSelect = true,
  showYearDropdown = true,
  clearable = true
}) => {
  const [field, meta, helpers] = useField<Date | null | undefined>(name);
  const { values } = useFormikContext<any>();
  const [active, setActive] = useState(false);
  const startRef: Ref<ReactDatePicker> = useRef(null);
  const selectedValue: Date | null = field.value ?? null;
  const otherValue: Date | null = (otherName && values[otherName]) ?? null;
  const otherProps: Partial<ReactDatePickerProps> = {};
  const inputId = name + "-input";

  switch (rangeType) {
    case DatePickerRangeType.NONE:
      otherProps.minDate = minDate;
      otherProps.maxDate = maxDate;
      otherProps.minTime = minTime;
      otherProps.maxTime = maxTime;
      break;
    case DatePickerRangeType.START:
      otherProps.startDate = selectedValue;
      otherProps.endDate = otherValue;
      otherProps.minDate = minDate;
      otherProps.maxDate = otherValue;
      otherProps.minTime = minTime ?? MIN_RANGE_TIME;
      otherProps.maxTime =
        otherValue && isSameDay(selectedValue, otherValue)
          ? otherValue
          : MAX_RANGE_TIME;
      otherProps.selectsStart = true;
      break;
    case DatePickerRangeType.END:
      otherProps.startDate = otherValue;
      otherProps.endDate = selectedValue;
      otherProps.minDate = otherValue;
      otherProps.maxDate = maxDate;
      otherProps.minTime =
        otherValue && isSameDay(selectedValue, otherValue)
          ? otherValue
          : MIN_RANGE_TIME;
      otherProps.maxTime = maxDate ?? MAX_RANGE_TIME;
      otherProps.selectsEnd = true;
      break;
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setActive(true);

    const onFocus = otherProps.onFocus;
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target;
    setActive(target.value.length > 0);

    const fieldOnBlur = field.onBlur;
    if (fieldOnBlur) {
      fieldOnBlur(event);
    }

    const onBlur = otherProps.onBlur;
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = (date: Date | null) => {
    if (date && otherValue) {
      switch (rangeType) {
        case DatePickerRangeType.START:
          if (date > otherValue) {
            date = otherValue;
          }
          break;
        case DatePickerRangeType.END:
          if (date < otherValue) {
            date = otherValue;
          }
          break;
      }
    }
    helpers.setValue(date ?? null);
  };

  const handleIconClick = () => {
    helpers.setValue(null);
    setActive(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.currentTarget.id === inputId && event.key === "Tab") {
      startRef.current?.setOpen(false);
    }
  };
  const selected = !!field.value;

  const wrapClasses: Array<string> = [
    label ? "floating-label-input" : "form-input"
  ];

  if (meta.touched && meta.error) {
    wrapClasses.push("has-error");
  }

  // Show active when there is a displayable value
  if (active || selected) {
    wrapClasses.push("active");
  }

  const ariaLabel = label || name;
  const classes = [disabled || readOnly ? "disabled" : ""]
    .filter(c => !!c)
    .join(" ");

  /*
   * Render using React Date Picker component.
   *
   * IMPORTANT NOTE:
   * ReactDatePicker will pass all applicable input based properties to the input component automatically. If this
   * ever becomes an issue, then a custom input component can be used and initialized the desired properties.
   *
   * See:
   * https://github.com/Hacker0x01/react-datepicker/blob/master/docs/datepicker.md
   */
  return (
    <div className={wrapClasses.join(" ")}>
      <div className="datepicker">
        {label && (
          <label className="floating-label" htmlFor={inputId}>
            {label}
          </label>
        )}
        <div className="input-wrapper">
          <span className="toggle-area">
            <ReactDatePicker
              ref={startRef}
              name={name}
              id={inputId}
              aria-label={ariaLabel}
              className={classes}
              popperClassName="poly-datepicker"
              wrapperClassName="poly-datepicker-wrapper"
              data-testid="value-input"
              tabIndex={tabIndex || -1}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              readOnly={readOnly}
              autoComplete={"off"}
              isClearable={false}
              showMonthDropdown={showMonthDropdown}
              showTimeSelect={showTimeSelect}
              showYearDropdown={showYearDropdown}
              showTimeInput={false}
              dateFormat="M/d/yyyy h:mm:ss aa"
              timeFormat="h:mm:ss aa"
              popperProps={{
                strategy: "fixed"
              }}
              popperModifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 6]
                  }
                }
              ]}
              selected={selectedValue}
              {...otherProps}
            />
          </span>
          {!(disabled || readOnly) && clearable && selected && (
            <span
              className="icon"
              onClick={handleIconClick}
              data-testid="action-icon"
            >
              <PolyIcon icon="clear_x" />
            </span>
          )}
        </div>
      </div>
      <ErrorMessage
        component="div"
        className="error-message"
        data-testid={`error-msg-for-${name}`}
        name={name}
      />
    </div>
  );
};

export interface DatePickerRangeProps {
  startName: string;
  endName: string;
  startLabel?: string;
  endLabel?: string;
  startTabIndex?: number;
  endTabIndex?: number;
  minDate?: Date;
  maxDate?: Date;
  minTime?: Date;
  maxTime?: Date;
  showMonthDropdown?: boolean;
  showTimeSelect?: boolean;
  showYearDropdown?: boolean;
  clearable?: boolean;
  className?: string;
}

export const DateRangePicker: React.FC<DatePickerRangeProps> = ({
  startLabel,
  endLabel,
  startName,
  endName,
  startTabIndex,
  endTabIndex,
  className,
  showMonthDropdown = true,
  showTimeSelect = true,
  showYearDropdown = true,
  ...otherProps
}) => {
  const aStartTabIndex = startTabIndex || (endTabIndex && endTabIndex - 1);

  const aEndTabIndex = endTabIndex || (startTabIndex && startTabIndex + 1);

  return (
    <div className={className ?? ""}>
      <DatePicker
        name={startName}
        otherName={endName}
        rangeType={DatePickerRangeType.START}
        label={startLabel}
        tabIndex={aStartTabIndex}
        showMonthDropdown={showMonthDropdown}
        showTimeSelect={showTimeSelect}
        showYearDropdown={showYearDropdown}
        {...otherProps}
      />
      <DatePicker
        name={endName}
        otherName={startName}
        rangeType={DatePickerRangeType.END}
        label={endLabel}
        tabIndex={aEndTabIndex}
        showTimeSelect={showTimeSelect}
        {...otherProps}
      />
    </div>
  );
};
